"use client";
import React, { useState, useMemo } from "react";
import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { FaWhatsapp } from "react-icons/fa";
import {
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ConsultationForm from "@/components/ConsultationForm";
import CountrySelector from "@/components/CountrySelector/CountrySelector";
import TestimonialVideos from "@/components/TestimonialVideos/TestimonialVideos";
import SectionCorousel from "./SectionCorousel";
import PointsContainer from "@/components/PointsContainer/PointsContainer";
import "../../doctors/[doctorinfo]/doctorinfo.scss";

const inclusions = [
  "Preoperative tests (blood, X-ray, ECG)",
  "Surgery cost (based on implant type)",
  "Types of Implant (Ceramic, Hybrid, etc.)",
];

const exclusions = [
  "Travel expenses not covered",
  "Post-treatment physiotherapy sessions",
  "Accommodation beyond standard stay",
];
const Complementary = [
  "Free initial consultation with surgeon",
  "Complimentary airport transfers",
  "Translation services",
];

const PackageDetails = ({ treatmentPackage, packageDetails, country }) => {
  const [selectedCountry, setSelectedCountry] = useState(country || "India");
  const [currentHospitalPage, setCurrentHospitalPage] = useState(0);
  const carouselRef = useRef(null);

  // Helper functions for the hospital carousel
  const hospitalData =
    treatmentPackage?.countryWisePrice?.[0]?.top_hospitals?.length > 0
      ? treatmentPackage.countryWisePrice[0].top_hospitals
      : [
          {
            id: "placeholder-1",
            name: "BLK-Max Super Speciality Hospital Delhi",
            address:
              "Pusa Rd, Radha Soami Satsang, Rajendra Place New Delhi 110005",
          },
          {
            id: "placeholder-2",
            name: "Manipal Hospital Bangalore",
            address: "98, HAL Airport Road, Bengaluru, Karnataka 560017",
          },
          {
            id: "placeholder-3",
            name: "Fortis Hospital Gurgaon",
            address:
              "Sector 44, Opposite HUDA City Centre, Gurugram, Haryana 122002",
          },
          {
            id: "placeholder-4",
            name: "Apollo Hospital Chennai",
            address:
              "21, Greams Lane, Off Greams Road, Chennai, Tamil Nadu 600006",
          },
        ];

  const totalHospitalPages = Math.ceil(hospitalData.length / 2);

  const scrollToCard = (index) => {
    const container = carouselRef.current;
    if (!container) return;

    const card = container.children[0]?.children[index];
    if (!card) return;

    const containerWidth = container.offsetWidth;
    const cardWidth = card.offsetWidth;
    const scrollLeft = (cardWidth + 16) * index; // 16px is the gap between cards

    container.scrollTo({
      left: scrollLeft - (containerWidth - cardWidth) / 2,
      behavior: "smooth",
    });
  };

  const nextHospitalPage = () => {
    const newPage =
      currentHospitalPage === visibleHospitals.length - 1
        ? 0
        : currentHospitalPage + 1;
    setCurrentHospitalPage(newPage);
    setTimeout(() => scrollToCard(newPage), 10);
  };

  const prevHospitalPage = () => {
    const newPage =
      currentHospitalPage === 0
        ? visibleHospitals.length - 1
        : currentHospitalPage - 1;
    setCurrentHospitalPage(newPage);
    setTimeout(() => scrollToCard(newPage), 10);
  };

  // Show all hospitals in the carousel
  const visibleHospitals = [...hospitalData];

  // Keep doctor carousel stepping aligned at lg/xl widths.
  const getDoctorsCarouselScrollStep = () => {
    if (typeof window === "undefined") return 300;
    if (window.innerWidth >= 1280) return 344;
    if (window.innerWidth >= 1024) return 296;
    return 300;
  };

  // Use the exact same video from the home page
  const patientTestimonials = useMemo(() => {
    // First check if there are related videos in the treatment package
    if (
      packageDetails?.relatedVideos &&
      packageDetails.relatedVideos.length > 0
    ) {
      return packageDetails.relatedVideos.map((video) => ({
        id: video.id,
        title: video.title || video.heading,
        url: video.url || video.videoLink,
        thumbnail: video.thumbnail || (video.img && video.img.url),
      }));
    }

    // Home page videos as specified by the user
    return [
      {
        id: 1,
        title: "Patient Testimonial - FitSib Health",
        url: "https://www.youtube.com/watch?v=67KA0VJW4pE",
        thumbnail: "https://img.youtube.com/vi/67KA0VJW4pE/maxresdefault.jpg",
      },
      {
        id: 2,
        title: "Patient Recovery Story - FitSib Health",
        url: "https://www.youtube.com/watch?v=xx7qOTF3Hdo",
        thumbnail: "https://img.youtube.com/vi/xx7qOTF3Hdo/maxresdefault.jpg",
      },
      {
        id: 3,
        title: "Medical Care Experience - FitSib Health",
        url: "https://www.youtube.com/watch?v=RH-jyymMSuk",
        thumbnail: "https://img.youtube.com/vi/RH-jyymMSuk/maxresdefault.jpg",
      },
    ];
  }, [packageDetails]);

  // FAQ data based on package type
  const packageFAQs = packageDetails?.faqData || [];

  // Heading component for Why Choose Us section
  const whyChooseUsHeading = () => {
    return (
      <>
        <div className="boldHeading">Why Choose Us?</div>
      </>
    );
  };

  return (
    <div className="w-full overflow-x-hidden">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-4 lg:px-5 flex flex-col lg:flex-row w-full gap-4 md:gap-6 lg:gap-5">
        <div className="flex-1 min-w-0">
          {/* Breadcrumbs */}
          <div className="w-full px-4 md:px-0">
            <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm my-3 md:my-4">
              <Link href="/" className="text-[#006DAF] whitespace-nowrap">
                Home
              </Link>
              <span>/</span>
              <Link
                href="/packages"
                className="text-[#006DAF] whitespace-nowrap"
              >
                Packages
              </Link>
              <span>/</span>
              <span className="text-gray-500 break-words">
                {treatmentPackage.packageName}
              </span>
            </div>
          </div>
          <div className="w-full max-w-[1350px] h-auto mt-1.5 md:mt-[6px] ml-0 md:ml-[8px] lg:ml-0 rounded-none md:rounded-[20px] border-0 md:border border-solid border-gray-200 overflow-hidden">
            {/* Mobile Consultation Form - moved to top */}
           {/*} <div className="md:hidden w-full px-4 mt-4 mb-6">
              <div className="w-full border border-[#E4E8FF] rounded-lg overflow-hidden bg-white">
                <ConsultationForm selectedCountry={selectedCountry?.country} />
              </div>
            </div>*/}

            {/* Country Selector - Mobile */}
            <div className="md:hidden w-full">
              <CountrySelector
                onCountryChange={setSelectedCountry}
                initialCountry={selectedCountry}
                title={"Select Your Country for treatment packages"}
              />
            </div>

            {/* Mobile Package Title - only shown on mobile */}
            <div className="md:hidden px-4 mt-4">
              <h1 className="text-2xl font-bold text-[#006DAF] leading-tight">
                {treatmentPackage.packageName} in {selectedCountry?.country}
              </h1>
            </div>


            {/* About Section - Mobile - appears right after form on mobile */}
            <div className="md:hidden w-full mb-6 px-4">
              {/* Mobile About Image */}
              <div className="relative w-full max-w-[320px] h-[277px] rounded-[20px] overflow-hidden border border-[#E4E8FF] bg-white shadow-[0px_2px_4px_0px_#0000001A] mx-auto mb-6">
                <Image
                  src={
                    treatmentPackage.img?.url ||
                    "/placeholder.svg?height=400&width=500"
                  }
                  alt={treatmentPackage.packageName}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 100vw, 320px"
                  style={{
                    borderRadius: "20px",
                    padding: "1px",
                  }}
                />
              </div>

              {/* Mobile About Text */}
              <div className="w-full px-4">
                <div className="w-full mb-4">
                  <h2 className="text-[28px] font-bold leading-[40px] tracking-[0.04em] font-poppins text-[#006DAF] mb-1 text-center">
                    About
                  </h2>
                  <h3 className="text-[26px] leading-[40px] tracking-[0.04em] mt-[-5px] font-poppins text-gray-500 text-center">
                    The <span className="font-[200]">Package</span>
                  </h3>
                </div>

                <p
                  className="text-[#9AA3B7] text-[12px] leading-[24px] tracking-[0.04em] font-poppins font-normal text-center mb-6"
                  style={{ letterSpacing: "0.04em" }}
                >
                  {treatmentPackage.description ||
                    "Total Hip Replacement Surgery (THR)/bilateral is a procedure where a surgeon removes the damaged sections of both the hip joints and replaces them with parts/implants usually constructed of metal, ceramic and very hard plastic. Hip replacement surgery might be an option if hip pain interferes with daily activities and nonsurgical treatments haven't helped or are no longer effective. Arthritis damage is the most common reason to need hip replacement."}
                </p>
                <div className="flex justify-center">
                  <button className="w-[180px] h-[42px] rounded-[21px] border border-[#F0F0F0] bg-white text-[#006DAF] flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <span className="font-medium">Show More</span>
                    <span className="ml-2 text-lg font-light">+</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout - original layout for larger screens */}
            <div className="hidden md:block">
              {/* Country Selector - Desktop */}
              <div className="hidden md:block">
                <CountrySelector
                  onCountryChange={setSelectedCountry}
                  initialCountry={selectedCountry}
                  title={"Select Your Country for treatment packages"}
                />
              </div>

              {/* Desktop Form - will be moved to sidebar */}

              {/* Original Package Title - only shown on desktop */}
              <h1 className="text-2xl lg:text-[28px] xl:text-3xl font-bold text-[#006DAF] mb-5 md:mb-7 lg:mb-6 leading-tight px-5 sm:px-6 lg:px-7 xl:px-8">
                {treatmentPackage.packageName} in {selectedCountry?.country}
              </h1>

              {/* Rest of your content... */}
            </div>
            {/* About Section - Desktop */}
            <div className="hidden md:block pt-0 pb-6 md:pb-8 lg:pb-10 px-0">
              <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-5 xl:gap-8 items-center lg:items-start w-full max-w-[min(100%,1100px)] lg:mx-auto">
                {/* Content Column */}
                <div className="space-y-5 pl-5 md:pl-6 lg:pl-5 xl:pl-8 pr-0 md:pr-3 lg:pr-2 xl:pr-4 min-w-0">
                  <div className="mb-4">
                    <h2 className="text-[28px] md:text-[30px] lg:text-[28px] xl:text-[32px] font-bold leading-tight lg:leading-[34px] xl:leading-[36px] tracking-[0.04em] font-poppins text-[#006DAF] mb-0">
                      About
                    </h2>
                    <h3 className="text-[26px] md:text-[28px] lg:text-[26px] xl:text-[30px] leading-tight lg:leading-[32px] xl:leading-[36px] tracking-[0.04em] font-poppins text-gray-500 -mt-1">
                      The <span className="font-light">Package</span>
                    </h3>
                  </div>

                  <p className="text-[#9AA3B7] text-[14px] lg:text-[15px] leading-[22px] lg:leading-[24px] tracking-[0.04em] font-poppins font-normal max-w-lg lg:max-w-none pr-2">
                    {treatmentPackage.description ||
                      "Total Hip Replacement Surgery (THR)/bilateral is a procedure where a surgeon removes the damaged sections of both the hip joints and replaces them with parts/implants usually constructed of metal, ceramic and very hard plastic. Hip replacement surgery might be an option if hip pain interferes with daily activities and nonsurgical treatments haven't helped or are no longer effective. Arthritis damage is the most common reason to need hip replacement."}
                  </p>

                  <div className="pt-2">
                    <button className="w-[210px] h-[42px] rounded-[21px] border border-[#F0F0F0] bg-white text-[#006DAF] flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm">
                      <span className="font-medium">Show More</span>
                      <span className="ml-2 text-lg font-light">+</span>
                    </button>
                  </div>
                </div>

                {/* Image Column — slightly smaller at lg (1024) so the two-column row doesn’t crowd */}
                <div className="flex justify-center md:justify-end lg:justify-center xl:justify-end pl-0 md:pl-3 lg:pl-2 xl:pl-4 pr-4 md:pr-5 lg:pr-4 xl:pr-8 shrink-0 min-w-0">
                  <div className="relative w-full max-w-[300px] h-[340px] lg:max-w-[288px] lg:h-[328px] xl:max-w-[320px] xl:h-[360px] rounded-[20px] overflow-hidden border border-[#E4E8FF] bg-white shadow-[0px_4px_8px_0px_#0000001A]">
                    <Image
                      src={
                        treatmentPackage.img?.url ||
                        "/placeholder.svg?height=400&width=500"
                      }
                      alt={treatmentPackage.packageName}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1023px) 45vw, (max-width: 1279px) 288px, 320px"
                      style={{
                        borderRadius: "20px",
                        padding: "1px",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Treatment Cost Box - Mobile */}
            <div className="md:hidden px-4">
              <div className="w-full max-w-[320px] h-auto min-h-[360px] rounded-[20px] border border-[#E4E8FF] bg-white shadow-[0px_4px_16.3px_-2px_rgba(0,0,0,0.1)] p-4 mx-auto mb-8">
                <h3 className="text-[24px] font-bold text-[#006DAF] mb-4">
                  Treatment Cost ${selectedCountry?.packageCost || "8,000"}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="#00A791"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-[#00A791] font-medium text-sm">
                      Zero Hidden Costs
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="#00A791"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-[#00A791] font-medium text-sm">
                      5-Star Recovery Support
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="#00A791"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-[#00A791] text-sm font-medium">
                      99.9% Success Rate
                    </span>
                  </div>
                </div>
              </div>

              {/* Doctor image for mobile - placed in next line */}
              <div className="flex justify-center">
                <div className="w-[80px] h-[160px]">
                  <Image
                    src="/images/Capa_1.png"
                    alt="Doctor"
                    width={80}
                    height={160}
                    className="w-full h-full object-scale-down drop-shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
                  />
                </div>
              </div>
            </div>

            {/* Treatment Cost Box - Desktop */}
            <div className="hidden md:block bg-white rounded-xl shadow-[0px_0px_10px_#0000001A] p-4 md:p-6 lg:p-6 xl:p-8 mb-6 md:mb-8 lg:mb-10 border border-[#E4E8FF] relative overflow-hidden mx-4 sm:mx-5 md:mx-6 lg:mx-5 xl:mx-8 max-w-full">
              <div className="flex flex-col md:flex-row items-start md:items-end gap-4 md:gap-6 relative z-10">
                <div className="flex-1 min-w-0 pr-4 md:pr-6 lg:pr-[6.5rem] xl:pr-8">
                  <h3 className="font-[600] text-lg md:text-[20px] leading-[100%] tracking-[0.04em] font-poppins text-[#006DAF] mb-3 md:mb-5 mt-2">
                    Treatment Cost ${selectedCountry?.packageCost || "8,000"}
                  </h3>
                  <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 sm:gap-3 lg:gap-4">
                    <span className="text-[#00A791] font-medium text-sm md:text-base whitespace-nowrap">
                      Zero Hidden Costs
                    </span>
                    <div className="hidden sm:block h-5 w-[1px] bg-gray-300"></div>
                    <span className="text-[#00A791] font-medium text-sm md:text-base whitespace-nowrap">
                      5-Star Recovery Support
                    </span>
                    <div className="hidden sm:block h-5 w-[1px] bg-gray-300"></div>
                    <span className="text-[#00A791] font-medium text-sm md:text-base whitespace-nowrap">
                      99.9% Success Rate
                    </span>
                  </div>
                </div>
              </div>

              {/* Doctor image - hidden on mobile, positioned differently on desktop */}
              <div className="hidden lg:block absolute backdrop-blur-[100px] right-4 lg:right-5 xl:right-8 top-[-16px] lg:top-[-18px] xl:top-[-20px] w-[72px] lg:w-[76px] xl:w-[100px] h-[165px] lg:h-[172px] xl:h-[210px] z-20 pointer-events-none">
                <Image
                  src="/images/Capa_1.png"
                  alt="Doctor"
                  width={70}
                  height={140}
                  className="w-full h-full object-scale-down drop-shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                />
              </div>

              <button className="mt-3 md:mt-4 text-[#006DAF] font-medium text-sm md:text-base underline underline-offset-[3px] decoration-[#006DAF] decoration-2">
                Get a Free Consultation with Our Top Doctor
              </button>
            </div>

            {/* Why Choose Us Component */}
            {packageDetails?.whyChooseUs && packageDetails.whyChooseUs.length > 0 && (
              <PointsContainer
                heading={whyChooseUsHeading()}
                color={"#557AF9"}
                data={packageDetails.whyChooseUs.map((item) => item.title)}
                background={"linear-gradient(180deg, #E3F8FF 0%, #FBFDFE 100%)"}
                size="large"
              />
            )}

            {/* Package Details by Country */}
            {(packageDetails?.treatmentPackageDetailsWithCountry?.length > 0
              ? packageDetails.treatmentPackageDetailsWithCountry
              : [
                  {
                    id: "mock-country-detail",
                    inclusions: null,
                    exclusion: null,
                    complementary: null,
                  },
                ]
            ).map((countryDetail) => (
              <div
                key={countryDetail?.id || "mock-id"}
                className="mb-4 md:mb-8 -mx-4 px-4 md:mx-0 md:px-0"
              >
                {/* Inclusions Section */}
                <div className="w-full max-w-full md:max-w-[925px] lg:max-w-[900px] xl:max-w-[925px] min-h-[300px] md:min-h-[380px] lg:min-h-[400px] h-auto relative mb-8 md:mb-10 mx-auto">
                  <h2 className="text-[28px] md:text-[30px] lg:text-[32px] lg:ml-0 xl:ml-[-50px] leading-[38px] tracking-[0.04em] font-poppins text-center mb-3 md:mb-4 px-1">
                    <span className="font-light " style={{ fontWeight: 275 }}>
                      Inclusions in the
                    </span>{" "}
                    <span
                      className="font-bold text-[#006DAF]"
                      style={{ fontWeight: 700 }}
                    >
                      package
                    </span>
                  </h2>
                  <p className="text-[12px] leading-[24px] tracking-[0.04em] text-[#4A536A] font-poppins font-normal text-center mb-6 md:mb-12 px-2">
                    The cost of {treatmentPackage.packageName} includes:
                  </p>

                  <div className="relative overflow-hidden">
                    <SectionCorousel
                      items={
                        countryDetail?.inclusions?.map((item) => item.name) ||
                        []
                      }
                      className="mt-[-10px] md:mt-[-40px]"
                    />
                  </div>

                  <p className="hidden md:block w-full max-w-[657px] mx-auto text-[12px] leading-[24px] tracking-[0.04em] text-[#4A536A] font-poppins text-center mt-8 px-4">
                    <span className="font-bold">Note: </span>Complete recovery
                    from {treatmentPackage.packageName} can be expected within
                    30 - 45 days.
                  </p>
                </div>

                {/* Exclusions Section */}
                <div className="w-full max-w-full md:max-w-[925px] lg:max-w-[900px] xl:max-w-[925px] min-h-[360px] md:min-h-[420px] lg:min-h-[440px] h-auto relative mb-1 md:mb-3 mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#E3F8FF] to-[#FBFDFE] -mx-4 md:mx-0 md:rounded-none z-0"></div>
                  <div className="relative z-10 pt-6 md:pt-1 px-4 md:px-0">
                    <h2 className="text-[28px] md:text-[30px] lg:text-[32px] lg:ml-0 xl:ml-[-50px] leading-[34px] md:leading-[38px] tracking-[0.04em] font-poppins text-center mb-3 md:mb-4 px-1">
                      <span className="font-light " style={{ fontWeight: 275 }}>
                        Exclusions in the
                      </span>{" "}
                      <span
                        className="font-bold text-[#006DAF]"
                        style={{ fontWeight: 700 }}
                      >
                        package
                      </span>
                    </h2>
                    <p className="text-[12px] leading-[24px] tracking-[0.04em] text-[#4A536A] font-poppins font-normal text-center mb-6 md:mb-12 px-2">
                      The following are not included in the cost of{" "}
                      {treatmentPackage.packageName}:
                    </p>

                    <div className="relative overflow-hidden">
                      <SectionCorousel
                        items={
                          countryDetail?.exclusion?.map((item) => item.name) || []
                        }
                        className=""
                      />
                    </div>

                    <p className="hidden md:block w-full max-w-[657px] mx-auto text-[12px] leading-[24px] tracking-[0.04em] text-[#4A536A] font-poppins text-center  px-4">
                      <span className="font-bold">Note: </span>Complete recovery
                      from {treatmentPackage.packageName} can be expected within
                      30 - 45 days.
                    </p>
                  </div>
                </div>

                {/* Complementary Section */}
                <div className="w-full max-w-full md:max-w-[925px] lg:max-w-[900px] xl:max-w-[925px] min-h-[360px] md:min-h-[420px] lg:min-h-[440px] h-auto relative mb-8 md:mb-10 mx-auto">
                  <div className="relative pt-2 md:pt-4">
                    <h2 className="text-[28px] md:text-[30px] lg:text-[32px] lg:ml-0 xl:ml-[-50px] leading-[38px] tracking-[0.04em] font-poppins text-center mb-3 md:mb-4 px-1">
                      <span className="font-light" style={{ fontWeight: 275 }}>
                        Complimentary in the
                      </span>{" "}
                      <span
                        className="font-bold text-[#006DAF]"
                        style={{ fontWeight: 700 }}
                      >
                        package
                      </span>
                    </h2>
                    <p className="text-[12px] leading-[24px] tracking-[0.04em] text-[#4A536A] font-poppins font-normal text-center mb-6 md:mb-12 px-2">
                      The following are included as complimentary with{" "}
                      {treatmentPackage.packageName}:
                    </p>

                    <div className="relative overflow-hidden">
                      <SectionCorousel
                        items={
                          countryDetail?.complimentory?.map(
                            (item) => item.name
                          ) || []
                        }
                        className="mb-8 sm:mb-0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Doctors Section - Moved to appear below complementary section */}
            <div className="w-full mx-auto mb-4 md:mb-8 bg-gradient-to-b from-[#E3F8FF] to-[#FBFDFE] py-8 md:py-10 relative overflow-hidden">
              <h2 className="text-2xl sm:text-3xl lg:text-[28px] xl:text-3xl font-bold text-center mb-8 md:mb-10 px-4 sm:px-6 md:px-8 lg:px-10">
                Meet Our <span className="text-[#006DAF]">Doctors</span>
              </h2>
              <div className="w-full max-w-7xl mx-auto relative px-4 sm:px-6 md:px-6 lg:px-8 xl:px-10">
                <div className="relative">
                  <button
                    onClick={() => {
                      const container =
                        document.querySelector(".doctors-carousel");
                      if (container) {
                        container.scrollBy({
                          left: -getDoctorsCarouselScrollStep(),
                          behavior: "smooth",
                        });
                      }
                    }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 md:-translate-x-5 lg:-translate-x-3 xl:-translate-x-6 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white border border-[#DFE6FF] shadow-lg hover:bg-gray-50 flex items-center justify-center z-20"
                  >
                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                  <div
                    className="doctors-carousel flex gap-3 sm:gap-5 lg:gap-4 xl:gap-6 overflow-x-auto pb-4 scroll-smooth px-3 sm:px-4 lg:px-12 xl:px-14"
                    style={{
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      scrollSnapType: "x mandatory",
                      justifyContent: "flex-start",
                    }} // Hide scrollbar
                  >
                    {(() => {
                      // Get doctors from API or fallback to empty array
                      const apiDoctors =
                        treatmentPackage.countryWisePrice?.[0]?.top_doctors ||
                        [];

                      // Check if this is an orthopedic surgery package (knee or hip replacement)
                      const packageName =
                        treatmentPackage.packageName?.toLowerCase() || "";
                      const isOrthopedicSurgery =
                        packageName.includes("knee") ||
                        packageName.includes("hip") ||
                        packageName.includes("joint");

                      let filteredDoctors = apiDoctors;

                      if (isOrthopedicSurgery) {
                        // For orthopedic surgeries, show orthopedic specialists
                        filteredDoctors = apiDoctors.filter(
                          (doctor) =>
                            doctor.Department &&
                            doctor.Department.toLowerCase().includes("ortho")
                        );
                      } else {
                        // For other packages, show cardiologists
                        filteredDoctors = apiDoctors.filter(
                          (doctor) =>
                            doctor.Department &&
                            doctor.Department.toLowerCase().includes("cardio")
                        );
                      }

                      // Use filtered doctors, or all doctors if no matches found
                      const doctorsToShow =
                        filteredDoctors.length > 0
                          ? filteredDoctors
                          : apiDoctors;

                      return doctorsToShow.map((doctor) => (
                        <div
                          key={doctor.id}
                          className="bg-white rounded-xl shadow-md w-[240px] sm:w-[260px] md:w-[280px] lg:w-[280px] xl:w-[320px] h-auto min-h-[400px] md:min-h-[440px] lg:min-h-[450px] flex-shrink-0 overflow-hidden snap-center flex flex-col"
                          style={{
                            minWidth: "240px",
                            maxWidth: "320px",
                            flexShrink: 0,
                          }}
                        >
                          <div className="p-5 text-center flex flex-col h-full">
                            {/* Doctor Profile Image */}
                            <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4 border-3 border-[#E3F8FF] flex-shrink-0">
                              <Image
                                src={
                                  doctor.profile?.url ||
                                  "/images/heart-valve.jpg"
                                }
                                alt={doctor.name}
                                width={96}
                                height={96}
                                className="object-cover w-full h-full"
                              />
                            </div>

                            {/* Doctor Info */}
                            <div className="flex-1 flex flex-col justify-between">
                              <div className="mb-4">
                                <h3 className="font-poppins font-semibold text-base leading-tight text-center mb-2 text-gray-900">
                                  {doctor.name}
                                </h3>
                                <p className="font-poppins font-medium text-sm leading-tight text-center text-[#4A536A] mb-3">
                                  {doctor.Department ||
                                    doctor.Designation ||
                                    "Specialist"}
                                </p>
                              </div>

                              {/* WhatsApp Chat Button */}
                              <div className="mb-4">
                                <button className="flex items-center justify-center font-poppins bg-[#4ade80] hover:bg-[#3fcb75] transition-colors text-white w-28 h-10 rounded-md mx-auto">
                                  <FaWhatsapp className="w-4 h-4 mr-2" />
                                  <span className="text-sm font-medium">
                                    Chat
                                  </span>
                                </button>
                              </div>

                              {/* Hospital & Experience */}
                              <div className="font-poppins text-center text-[#4A536A] mb-4">
                                <p className="text-xs leading-tight mb-2">
                                  {doctor.institute ||
                                    "Fortis Hospital, Bangalore"}
                                </p>
                                <p className="font-semibold text-sm text-gray-900">
                                  {doctor.experiance} years of experience
                                </p>
                              </div>
                            </div>

                            {/* Contact Doctor Button */}
                            <div className="mt-auto pt-2">
                              <Link
                                href={`/doctors/${doctor.slug || doctor.id}`}
                                className="w-full max-w-[200px] mx-auto font-poppins font-semibold text-sm h-12 flex items-center justify-center rounded-xl border border-[#E4E8FF] text-[#006DAF] hover:bg-[#F5FAFF] transition-colors"
                              >
                                Contact Doctor
                              </Link>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                  <button
                    onClick={() => {
                      const container =
                        document.querySelector(".doctors-carousel");
                      if (container) {
                        container.scrollBy({
                          left: getDoctorsCarouselScrollStep(),
                          behavior: "smooth",
                        });
                      }
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 md:translate-x-5 lg:translate-x-3 xl:translate-x-6 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white border border-[#DFE6FF] shadow-lg hover:bg-gray-50 flex items-center justify-center z-20"
                  >
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                </div>
                <div className="flex justify-center mt-6 md:mt-7">
                  <Link
                    href="/doctors"
                    className="w-full max-w-[280px] h-[60px] flex items-center justify-center rounded-[30px] border border-[#006DAF] text-[#006DAF] font-medium hover:bg-[#006DAF] hover:text-white transition-colors mx-auto"
                  >
                    See More Doctors
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-6 md:mt-8 mb-4 md:mb-6 px-2 sm:px-0">
              <h2 className="text-xl sm:text-2xl lg:text-[26px] xl:text-3xl font-bold text-center mb-6 max-w-[min(100%,52rem)] mx-auto leading-snug">
                <span className="text-[#006DAF]">
                  Top Hospitals for {treatmentPackage.packageName}{" "}
                </span>
                <span className="text-gray-600">
                  in {selectedCountry?.country || "India"}
                </span>
              </h2>
            </div>

            <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-6 lg:px-8 overflow-hidden mt-1 md:mt-[4px]">
              {/* Left Arrow - Visible on all screens */}
              <button
                onClick={prevHospitalPage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white border border-[#DFE6FF] shadow-lg hover:bg-gray-50 flex items-center justify-center z-20"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>

              {/* Carousel */}
              <div
                ref={carouselRef}
                className="w-full mx-auto overflow-x-auto pb-4 scrollbar-hide scroll-px-3 lg:scroll-px-4"
                style={{
                  scrollBehavior: "smooth",
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                  WebkitOverflowScrolling: "touch",
                  scrollSnapType: "x mandatory",
                }}
              >
                <div
                  className="flex gap-4 sm:gap-6 px-4 lg:px-12 xl:px-14 justify-center md:justify-start"
                  style={{
                    width: "100%",
                  }}
                >
                  {(() => {
                    // Get all hospitals from API or fallback to empty array
                    const apiHospitals =
                      treatmentPackage.countryWisePrice?.[0]?.top_hospitals ||
                      [];

                    // Check if this is an orthopedic surgery package (knee or hip replacement)
                    const packageName =
                      treatmentPackage.packageName?.toLowerCase() || "";
                    const isOrthopedicSurgery =
                      packageName.includes("knee") ||
                      packageName.includes("hip") ||
                      packageName.includes("joint");

                    // Filter hospitals based on package type
                    let filteredHospitals = apiHospitals;

                    if (isOrthopedicSurgery) {
                      // For orthopedic surgeries, filter out heart-specific hospitals
                      filteredHospitals = apiHospitals.filter(
                        (hospital) =>
                          !hospital.name?.toLowerCase().includes("heart") &&
                          !hospital.name?.toLowerCase().includes("cardiac")
                      );
                    } else {
                      // For cardiac packages, show heart-related hospitals first
                      filteredHospitals = [...apiHospitals].sort((a, b) => {
                        const aIsCardiac =
                          a.name?.toLowerCase().includes("heart") ||
                          a.name?.toLowerCase().includes("cardiac");
                        const bIsCardiac =
                          b.name?.toLowerCase().includes("heart") ||
                          b.name?.toLowerCase().includes("cardiac");
                        return (bIsCardiac ? 1 : 0) - (aIsCardiac ? 1 : 0);
                      });
                    }

                    // Use filtered hospitals, or all hospitals if no matches found
                    const hospitalsToShow =
                      filteredHospitals.length > 0
                        ? filteredHospitals
                        : apiHospitals;

                    if (hospitalsToShow.length === 0) {
                      return (
                        <div className="w-full text-center py-8">
                          <p className="text-gray-500">
                            No hospitals available for this package in the
                            selected country.
                          </p>
                        </div>
                      );
                    }

                    return hospitalsToShow.map((hospital) => (
                      <div
                        key={hospital.id}
                        className="flex-shrink-0 w-[280px] sm:w-[300px] h-auto snap-center"
                        style={{
                          minWidth: "280px",
                          maxWidth: "300px",
                        }}
                      >
                        <div className="bg-white w-full h-full rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100 flex flex-col">
                          {/* Image */}
                          <div className="relative mx-auto mt-4 w-[90%] overflow-hidden rounded-lg">
                            <div className="relative w-full h-48">
                              <Image
                                className="w-full h-full object-cover"
                                src={
                                  hospital.img?.url || "/images/heart-valve.jpg"
                                }
                                alt={hospital.name || "Hospital"}
                                fill
                                sizes="300px"
                              />
                            </div>
                          </div>

                          {/* Info */}
                          <div className="p-4 flex flex-col h-full">
                            <div className="text-center mb-4">
                              <h3 className="text-base font-semibold mb-2 line-clamp-2 leading-tight text-gray-900">
                                {hospital.name || "Top Hospital"}
                              </h3>
                              <p className="text-gray-600 text-sm mb-3 line-clamp-2 leading-tight">
                                {hospital.address || hospital.place || "India"}
                              </p>
                            </div>

                            {/* Buttons */}
                            <div className="mt-auto">
                              <div className="flex gap-3">
                                <Link
                                  href={`/hospitals/${hospital.slug || hospital.id}`}
                                  className="flex-1 h-10 flex items-center justify-center border border-[#006DAF] text-[#006DAF] rounded-lg font-medium text-sm hover:bg-[#F5FAFF] transition-colors"
                                >
                                  Contact Hospital
                                </Link>
                                <button className="flex-1 h-10 bg-[#67C15E] text-white rounded-lg hover:bg-[#5AB854] flex items-center justify-center gap-2 transition-colors text-sm font-medium">
                                  <svg
                                    className="w-4 h-4 flex-shrink-0"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                  </svg>
                                  <span>Chat</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Right Arrow - Visible on all screens */}
              <button
                onClick={nextHospitalPage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white border border-[#DFE6FF] shadow-lg hover:bg-gray-50 flex items-center justify-center z-20"
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>

              {/* See More Hospitals Button - Moved outside carousel */}
              <div className="w-full flex justify-center my-6">
                <Link
                  href="/hospitals"
                  className="w-[280px] h-[60px] flex items-center justify-center rounded-[30px] border border-[#006DAF] text-[#006DAF] font-medium hover:bg-[#006DAF] hover:text-white transition-colors"
                >
                  See More Hospitals
                </Link>
              </div>
            </div>

            {/* International Patient Services Section */}
            <div className="w-full py-6 md:py-8 px-0 md:px-0 bg-gradient-to-b from-[#E3F8FF] to-[#FBFDFE] overflow-hidden">
              <div className="w-full flex flex-col items-center mb-6 md:mb-8">
                <h2 className="font-poppins font-semibold text-[20px] leading-[30px] tracking-wider text-center text-[#006DAF] md:text-[32px] md:inline">
                  <span className="block md:inline">International Patient</span>
                  <span className="block md:inline md:ml-1">Services</span>
                </h2>
              </div>
              <div className="flex flex-col md:flex-row flex-wrap justify-center gap-4 md:gap-5 lg:gap-6 mx-auto w-full max-w-[750px] lg:max-w-[920px] px-4 md:px-0">
                {/* Service 1 */}
                <div className="w-full max-w-[322px] min-h-[130px] md:w-[350px] md:min-h-[213px] bg-white rounded-[11.04px] md:rounded-xl border-[0.92px] md:border border-[#C3F1FF] p-4 md:p-8 relative shadow-[0px_0.92px_1.84px_5.52px_rgba(210,244,255,0.25)] mx-auto">
                  <div className="bg-[#006DAF] text-white w-7 h-7 md:w-[45px] md:h-[45px] rounded-full border-2 border-white flex items-center justify-center font-bold text-sm md:text-xl absolute -top-3 md:-top-[22.5px] left-4">
                    1
                  </div>
                  <h3 className="font-poppins font-medium text-base md:text-[20px] leading-5 md:leading-[30px] tracking-wider mt-2 md:mt-4 mb-1 md:mb-4 text-[#4A536A] line-clamp-2">
                    Advance bookings
                  </h3>
                  <p className="w-full font-poppins font-normal text-xs leading-4 tracking-wider text-gray-600 line-clamp-3 md:line-clamp-none md:text-base md:leading-[24px] md:mt-2">
                    Patients can share their medical reports online to discuss
                    their requirements and get treatment plans and quotes.
                  </p>
                </div>

                {/* Service 2 */}
                <div className="w-full max-w-[322px] min-h-[130px] md:w-[350px] md:min-h-[213px] bg-white rounded-[11.04px] md:rounded-xl border-[0.92px] md:border border-[#C3F1FF] p-4 md:p-8 relative shadow-[0px_0.92px_1.84px_5.52px_rgba(210,244,255,0.25)] mx-auto">
                  <div className="bg-[#006DAF] text-white w-7 h-7 md:w-[45px] md:h-[45px] rounded-full border-2 border-white flex items-center justify-center font-bold text-sm md:text-xl absolute -top-3 md:-top-[22.5px] left-4">
                    2
                  </div>
                  <h3 className="font-poppins font-medium text-base md:text-[20px] leading-5 md:leading-[30px] tracking-wider mt-2 md:mt-4 mb-1 md:mb-4 text-[#4A536A] line-clamp-2">
                    In Hospital Support
                  </h3>
                  <p className="w-full font-poppins font-normal text-xs leading-4 tracking-wider text-gray-600 line-clamp-3 md:line-clamp-none md:text-base md:leading-[24px] md:mt-2">
                    Dedicated support staff to assist you throughout your
                    hospital stay and treatment process.
                  </p>
                </div>

                {/* Service 3 */}
                <div className="w-full max-w-[322px] min-h-[130px] md:w-[350px] md:min-h-[213px] bg-white rounded-[11.04px] md:rounded-xl border-[0.92px] md:border border-[#C3F1FF] p-4 md:p-8 relative shadow-[0px_0.92px_1.84px_5.52px_rgba(210,244,255,0.25)] mx-auto">
                  <div className="bg-[#006DAF] text-white w-7 h-7 md:w-[45px] md:h-[45px] rounded-full border-2 border-white flex items-center justify-center font-bold text-sm md:text-xl absolute -top-3 md:-top-[22.5px] left-4">
                    3
                  </div>
                  <h3 className="font-poppins font-medium text-base md:text-[20px] leading-5 md:leading-[30px] tracking-wider mt-2 md:mt-4 mb-1 md:mb-4 text-[#4A536A] line-clamp-2">
                    Language Assistance
                  </h3>
                  <p className="w-full font-poppins font-normal text-xs leading-4 tracking-wider text-gray-600 line-clamp-3 md:line-clamp-none md:text-base md:leading-[24px] md:mt-2">
                    Professional interpreters available to assist with language
                    barriers during your treatment.
                  </p>
                </div>

                {/* Service 4 */}
                <div className="w-full max-w-[322px] min-h-[130px] md:w-[350px] md:min-h-[213px] bg-white rounded-[11.04px] md:rounded-xl border-[0.92px] md:border border-[#C3F1FF] p-4 md:p-8 relative shadow-[0px_0.92px_1.84px_5.52px_rgba(210,244,255,0.25)] mx-auto">
                  <div className="bg-[#006DAF] text-white w-7 h-7 md:w-[45px] md:h-[45px] rounded-full border-2 border-white flex items-center justify-center font-bold text-sm md:text-xl absolute -top-3 md:-top-[22.5px] left-4">
                    4
                  </div>
                  <h3 className="font-poppins font-medium text-base md:text-[20px] leading-5 md:leading-[30px] tracking-wider mt-2 md:mt-4 mb-1 md:mb-4 text-[#4A536A] line-clamp-2">
                    Post Surgery Care
                  </h3>
                  <p className="w-full font-poppins font-normal text-xs leading-4 tracking-wider text-gray-600 line-clamp-3 md:line-clamp-none md:text-base md:leading-[24px] md:mt-2">
                    Comprehensive post-operative care and follow-up
                    consultations for smooth recovery.
                  </p>
                </div>
              </div>
              <div className="flex justify-center mt-6 md:mt-7">
                <button className="w-full max-w-[280px] h-[60px] rounded-[30px] border border-[#006DAF] text-[#006DAF] font-medium text-lg hover:bg-[#006DAF] hover:text-white transition-colors duration-300 mx-auto">
                  Get FREE Opinion
                </button>
              </div>
            </div>

            {/* Patient Testimonials Section */}
            <div className="w-full mt-3 md:mt-4">
              <TestimonialVideos
                data={{ testimonialvideos: patientTestimonials }}
                heading="Success Stories from Our Patients"
              />

              {/* <div className="w-full flex justify-center mt-6 mb-10 sm:mt-8 sm:mb-8">
                <Link
                  href="/success-stories"
                  className="w-[280px] h-[60px] flex items-center justify-center rounded-full border border-[#006DAF] bg-white text-[#006DAF] font-medium hover:bg-[#006DAF] hover:text-white transition-colors"
                >
                  View All Testimonials
                </Link>
              </div> */}
            </div>

            {/* FAQ Section */}
            <div className="mb-8 md:mb-10 py-8 md:py-10 px-4 sm:px-6 lg:px-8 bg-[#EBF9FF]">
              <div className="container mx-auto">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl lg:text-[28px] xl:text-3xl font-bold text-center mb-2 px-2">
                    <span className="text-[#006DAF]">
                      Frequently asked questions
                    </span>{" "}
                    related to
                  </h2>
                  <p className="text-center text-lg sm:text-xl lg:text-lg xl:text-xl mb-8 lg:mb-10 px-2">
                    expenses that most patients have when planing for{" "}
                    {treatmentPackage.packageName.includes("Heart")
                      ? "Valve Replacement"
                      : "Knee Replacement"}
                  </p>

                  {/* Custom FAQ styling to match the figma design */}
                  <div className="faq-container">
                    {packageFAQs.map((faq, index) => (
                      <div
                        key={index}
                        className="mb-4 bg-white rounded-lg shadow overflow-hidden"
                      >
                        <details className="faq-item group">
                          <summary className="flex justify-between items-center p-5 cursor-pointer">
                            <span className="font-medium text-gray-700">
                              {faq.title}
                            </span>
                            <span className="text-[#006DAF] text-2xl font-bold group-open:transform group-open:rotate-45 transition-transform">
                              +
                            </span>
                          </summary>
                          <div className="p-5 pt-0 text-gray-600 border-t border-gray-100">
                            {faq.content}
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other sections... */}
        </div>

        {/* Desktop Form Sidebar - Sticky */}
       {/*} <div className="hidden lg:block flex-shrink-0 w-[400px] xl:w-[400px] lg:w-[350px] sticky top-6 h-fit z-10">
          <div className="border border-[#E4E8FF] rounded-lg overflow-hidden bg-white shadow-lg">
            <div className="w-full max-w-[560px] h-[700px] max-sm:h-auto mt-4 sm:mt-[-40px] xl:mt-[-80px] mx-auto bg-white border border-[#E4E8FF] rounded-lg p-4 max-sm:p-3 flex flex-col items-center justify-center
              xl:p-6 xl:mt-[-60px]
              lg:p-5 lg:mt-[-50px]
              md:p-4 md:mt-[-30px]
              sm:p-3
              p-2">
              <h2 className="font-poppins font-semibold text-base leading-[25px] tracking-[0.04em] text-center text-[#006DAF] mb-4 whitespace-nowrap max-sm:whitespace-normal
                xl:text-lg xl:mb-6
                lg:text-base lg:mb-5
                md:text-sm md:mb-4
                sm:text-sm sm:mb-3
                text-xs mb-2">
                Get in Touch with Our Medical Experts
              </h2>

              <div className="space-y-3 mt-[-10px] w-full max-w-[323.31px]
                xl:space-y-4 xl:max-w-[400px]
                lg:space-y-4 lg:max-w-[350px]
                md:space-y-3 md:max-w-[300px]
                sm:space-y-3 sm:max-w-[280px]
                space-y-2 max-w-[250px]">
                <div>
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full h-10 px-4 rounded-xl border-[#E4E8FF] placeholder-gray-400 bg-white
                      xl:h-11 xl:px-5
                      lg:h-10 lg:px-4
                      md:h-9 md:px-3
                      sm:h-8 sm:px-3
                      h-8 px-2
                      border focus:outline-none focus:ring-2 focus:ring-[#006DAF] focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full h-10 px-4 rounded-xl border-[#E4E8FF] placeholder-gray-400 bg-white
                      xl:h-11 xl:px-5
                      lg:h-10 lg:px-4
                      md:h-9 md:px-3
                      sm:h-8 sm:px-3
                      h-8 px-2
                      border focus:outline-none focus:ring-2 focus:ring-[#006DAF] focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    className="w-full h-10 px-4 rounded-xl border-[#E4E8FF] placeholder-gray-400 bg-white
                      xl:h-11 xl:px-5
                      lg:h-10 lg:px-4
                      md:h-9 md:px-3
                      sm:h-8 sm:px-3
                      h-8 px-2
                      border focus:outline-none focus:ring-2 focus:ring-[#006DAF] focus:border-transparent"
                  />
                </div>
                <div>
                  <select className="w-full h-10 px-4 rounded-xl border-[#E4E8FF] bg-white
                    xl:h-11 xl:px-5
                    lg:h-10 lg:px-4
                    md:h-9 md:px-3
                    sm:h-8 sm:px-3
                    h-8 px-2
                    border focus:outline-none focus:ring-2 focus:ring-[#006DAF] focus:border-transparent">
                    <option value="">Select Country</option>
                    <option value="India">India</option>
                    <option value="USA">USA</option>
                    <option value="UK">UK</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>
                <div>
                  <textarea
                    placeholder="Describe your medical condition"
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border-[#E4E8FF] placeholder-gray-400 bg-white resize-none
                      xl:px-5 xl:py-4
                      lg:px-4 lg:py-3
                      md:px-3 md:py-2
                      sm:px-3 sm:py-2
                      px-2 py-1
                      border focus:outline-none focus:ring-2 focus:ring-[#006DAF] focus:border-transparent"
                  />
                </div>
                <button className="w-full h-12 bg-[#006DAF] text-white rounded-xl font-medium hover:bg-[#005a8a] transition-colors
                  xl:h-14 xl:text-lg
                  lg:h-12 lg:text-base
                  md:h-10 md:text-sm
                  sm:h-9 sm:text-sm
                  h-8 text-xs">
                  Get Free Consultation
                </button>
              </div>
            </div>
          </div>
        </div>*/}
      </div>

    </div>
  );
};

export default PackageDetails;
